import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Reply, Trash2, Heart, ThumbsUp, Smile, AlertCircle, Angry, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { handleReaction, ReactionType, getReactionColor, getCurrentReaction, getReactionCounts } from '@/utils/postInteractions';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface Comment {
  id: string;
  content: string;
  created_at: string;
  parent_id?: string | null;
  user_id: string;
  profiles: {
    username: string;
    avatar_url: string | null;
  };
}

interface CommentWithReplies extends Comment {
  replies?: CommentWithReplies[];
}

interface PostCommentsProps {
  postId: string;
  currentUserId: string | undefined;
  comments: Comment[];
  onCommentAdded?: () => void;
  onCommentDeleted?: () => void;
}

export const PostComments = ({
  postId,
  currentUserId,
  comments: rawComments,
  onCommentAdded,
  onCommentDeleted
}: PostCommentsProps) => {
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyUsername, setReplyUsername] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const maxLength = 500;
  const [currentReactions, setCurrentReactions] = useState<{
    [key: string]: ReactionType | null;
  }>({});
  const [reactionCounts, setReactionCounts] = useState<{
    [key: string]: Array<{
      type: ReactionType;
      count: number;
    }>;
  }>({});
  const reactionIcons = {
    heart: Heart,
    thumbsUp: ThumbsUp,
    smile: Smile,
    surprised: AlertCircle,
    angry: Angry
  } as const;

  const formatUsername = (username: string) => {
    return username.toLowerCase().replace(/\s+/g, '_');
  };

  const comments = rawComments.reduce((acc: CommentWithReplies[], comment) => {
    if (!comment.parent_id) {
      const replies = rawComments
        .filter(c => c.parent_id === comment.id)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      return [...acc, { ...comment, replies }];
    }
    return acc;
  }, []);

  useEffect(() => {
    const initializeReactions = async () => {
      const reactions: {
        [key: string]: ReactionType | null;
      } = {};
      for (const comment of rawComments) {
        if (currentUserId) {
          const currentReaction = await getCurrentReaction(postId, currentUserId, comment.id);
          reactions[comment.id] = currentReaction;
        }
      }
      setCurrentReactions(reactions);
    };
    initializeReactions();
  }, [rawComments, currentUserId, postId]);

  const toggleReplies = (commentId: string) => {
    const newSet = new Set(expandedReplies);
    if (newSet.has(commentId)) {
      newSet.delete(commentId);
    } else {
      newSet.add(commentId);
    }
    setExpandedReplies(newSet);
  };

  const handleSubmit = async () => {
    if (!currentUserId || !newComment.trim()) return;
    try {
      setIsSubmitting(true);
      
      const commentContent = replyingTo 
        ? `@${replyUsername} ${newComment.trim()}`
        : newComment.trim();

      const { error } = await supabase.from('comments').insert([{
        post_id: postId,
        user_id: currentUserId,
        content: commentContent,
        parent_id: replyingTo || null
      }]);

      if (error) throw error;
      setNewComment("");
      setReplyingTo(null);
      setReplyUsername("");
      queryClient.invalidateQueries({ queryKey: ['post-comments'] });
      onCommentAdded?.();
      toast({
        title: "Success",
        description: "Comment added successfully"
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReplyClick = (comment: CommentWithReplies) => {
    const formattedUsername = formatUsername(comment.profiles.username);
    setReplyingTo(comment.id);
    setReplyUsername(formattedUsername);
    setNewComment(`@${formattedUsername} `);
  };

  const handleReactionClick = async (commentId: string, type: ReactionType) => {
    if (!currentUserId) {
      toast({
        title: "Authentication required",
        description: "Please sign in to react to comments",
        variant: "destructive"
      });
      return;
    }
    try {
      const newReaction = await handleReaction(postId, currentUserId, type, commentId);
      setCurrentReactions(prev => ({
        ...prev,
        [commentId]: newReaction
      }));
      const counts = await getReactionCounts(postId, commentId);
      setReactionCounts(prev => ({
        ...prev,
        [commentId]: counts
      }));
    } catch (error) {
      console.error('Error handling reaction:', error);
      toast({
        title: "Error",
        description: "Failed to update reaction",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!currentUserId) return;
    try {
      setIsDeleting(true);
      const { error } = await supabase.from('comments').delete().eq('id', commentId).eq('user_id', currentUserId);
      if (error) throw error;
      queryClient.invalidateQueries({
        queryKey: ['post-comments']
      });
      onCommentDeleted?.();
      toast({
        title: "Success",
        description: "Comment deleted successfully"
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const renderComment = (comment: CommentWithReplies, isReply = false, replyToUsername?: string) => {
    const isCommentOwner = currentUserId === comment.user_id;
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isExpanded = expandedReplies.has(comment.id);
    const currentReaction = currentReactions[comment.id] as keyof typeof reactionIcons | null;
    const ReactionIcon = currentReaction ? reactionIcons[currentReaction] : Heart;
    const formattedUsername = formatUsername(comment.profiles.username);
    const replyCount = comment.replies?.length || 0;

    return (
      <div key={comment.id} className={`space-y-4 ${isReply ? 'ml-12 mt-4 border-l-2 border-flux-cyan/20 pl-4' : ''}`}>
        <div className="flex gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={comment.profiles.avatar_url || undefined} />
            <AvatarFallback>{formattedUsername[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <Link to={`/profile/${comment.user_id}`} className="text-sm text-gray-400 hover:text-flux-cyan transition-colors">
              @{formattedUsername}
            </Link>
            <p className="text-white">
              {replyToUsername && (
                <span className="text-flux-cyan">@{replyToUsername} </span>
              )}
              {comment.content.replace(`@${replyToUsername} `, '')}
            </p>
            <div className="flex items-center gap-4 mt-2">
              <time className="text-xs text-gray-500">
                {new Date(comment.created_at).toLocaleString()}
              </time>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className={`p-0 h-auto ${currentReaction ? getReactionColor(currentReaction) : 'text-gray-400 hover:text-gray-300'}`}>
                    <ReactionIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-fit p-2 flex gap-2">
                  {Object.entries(reactionIcons).map(([type, Icon]) => <button key={type} onClick={() => handleReactionClick(comment.id, type as ReactionType)} className={`p-2 rounded-full transition-colors ${currentReaction === type ? getReactionColor(type as ReactionType) : 'text-gray-400 hover:text-gray-300'}`}>
                      <Icon className="w-4 h-4" />
                    </button>)}
                </PopoverContent>
              </Popover>

              {reactionCounts[comment.id]?.length > 0 && <HoverCard>
                  <HoverCardTrigger asChild>
                    <div className="flex items-center gap-2 cursor-pointer">
                      {reactionCounts[comment.id].slice(0, 3).map((reaction, index) => {
                    const Icon = reactionIcons[reaction.type];
                    return Icon ? <div key={reaction.type} className={`${getReactionColor(reaction.type)} bg-black/10 rounded-full p-1`}>
                            <Icon className="w-3 h-3" />
                          </div> : null;
                  })}
                      <span className="text-sm text-gray-400">
                        {reactionCounts[comment.id].reduce((sum, r) => sum + r.count, 0)}
                      </span>
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-60">
                    <div className="space-y-2">
                      {reactionCounts[comment.id].map(reaction => {
                    const Icon = reactionIcons[reaction.type];
                    return <div key={reaction.type} className="flex items-center gap-2">
                            {Icon && <Icon className={`w-4 h-4 ${getReactionColor(reaction.type)}`} />}
                            <span>{reaction.count}</span>
                          </div>;
                  })}
                    </div>
                  </HoverCardContent>
                </HoverCard>}

              {currentUserId && <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleReplyClick(comment)} 
                className="text-gray-400 hover:text-white p-0 h-auto"
              >
                <Reply className="h-3 w-3 mr-1" />
                Reply
              </Button>}

              {isCommentOwner && <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleDelete(comment.id)} 
                disabled={isDeleting} 
                className="text-red-500 hover:text-red-600 p-0 h-auto"
              >
                <Trash2 className="h-3 w-3" />
              </Button>}
            </div>
          </div>
        </div>

        {hasReplies && !isReply && (
          <div className="relative">
            <div 
              className="flex items-center gap-2 py-2 cursor-pointer text-sm text-gray-400 hover:text-gray-300"
              onClick={() => toggleReplies(comment.id)}
            >
              <div className="flex-1 border-t border-gray-700"></div>
              <span className="flex items-center gap-1">
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Hide replies
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Expand replies ({replyCount})
                  </>
                )}
              </span>
              <div className="flex-1 border-t border-gray-700"></div>
            </div>
            
            {isExpanded && (
              <div className="mt-2">
                {comment.replies?.map(reply => renderComment(reply, true, formattedUsername))}
              </div>
            )}
          </div>
        )}

        {replyingTo === comment.id && (
          <div className="ml-12 mt-2">
            <div className="space-y-2">
              <Textarea 
                value={newComment}
                onChange={e => {
                  if (e.target.value.length <= maxLength) {
                    setNewComment(e.target.value);
                  }
                }}
                placeholder={`Reply to @${formattedUsername}...`}
                className="bg-transparent border-flux-cyan/20 focus-visible:ring-flux-cyan/30 resize-none text-white"
                rows={3}
              />
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <span className={`text-sm ${newComment.length >= maxLength ? 'text-red-500' : 'text-gray-400'}`}>
                    {newComment.length}/{maxLength} characters
                  </span>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyUsername("");
                      setNewComment("");
                    }} 
                    className="text-gray-400"
                  >
                    Cancel Reply
                  </Button>
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={!newComment.trim() || isSubmitting || newComment.length > maxLength}
                  className="bg-flux-cyan hover:bg-flux-cyan/80 text-zinc-50 bg-gray-950 hover:bg-gray-800"
                >
                  Post Reply
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 mt-4 px-4">
      {currentUserId && !replyingTo && (
        <div className="space-y-2">
          <Textarea
            value={newComment}
            onChange={(e) => {
              if (e.target.value.length <= maxLength) {
                setNewComment(e.target.value);
              }
            }}
            placeholder="What are your thoughts?"
            className="bg-transparent border-flux-cyan/20 focus-visible:ring-flux-cyan/30 resize-none text-white"
            rows={3}
          />
          <div className="flex justify-between items-center">
            <span className={`text-sm ${
              newComment.length >= maxLength ? 'text-red-500' : 'text-gray-400'
            }`}>
              {newComment.length}/{maxLength} characters
            </span>
            <Button
              onClick={handleSubmit}
              disabled={!newComment.trim() || isSubmitting || newComment.length > maxLength}
              className="bg-flux-cyan hover:bg-flux-cyan/80 text-slate-50 bg-zinc-950 hover:bg-zinc-800"
            >
              Post Comment
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {comments.map(comment => renderComment(comment))}
      </div>
    </div>
  );
};
